import React, { useState, useRef, useMemo } from 'react';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import { Download, Upload, X, Loader2 } from 'lucide-react';
import { extractFundInvestorsImport } from '../../services/fundInvestorsImportService';
import { createFundInvestorLegacyImport } from '../../services/fundInvestorsService';

function isValidEmail(s) {
  if (!s || typeof s !== 'string') return false;
  const e = s.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

function formatMoney(n) {
  if (n == null || typeof n !== 'number' || Number.isNaN(n)) return '—';
  const s = String(Math.max(0, Math.floor(n * 100) / 100));
  const [intPart, dec] = s.includes('.') ? s.split('.') : [s, '00'];
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const d = (dec || '00').slice(0, 2).padEnd(2, '0');
  return `${withSpaces}.${d} $`;
}

function trancheKindLabel(kind, t) {
  if (kind === 'initial') return t('fundInvestors.fileImport.trancheInitial');
  if (kind === 'addition') return t('fundInvestors.fileImport.trancheAddition');
  return t('fundInvestors.fileImport.trancheUnspecified');
}

/** Same rules as functions/vertexAi.js — merge rows that share partner code or email. */
function canonicalPartnerCode(inv) {
  if (!inv || typeof inv !== 'object') return '';
  const ident = typeof inv.identifier === 'string' ? inv.identifier.trim() : '';
  const name = typeof inv.name === 'string' ? inv.name.trim() : '';
  const email = typeof inv.email === 'string' ? inv.email.trim().toLowerCase() : '';
  if (/^\d{3,10}$/.test(ident)) return ident;
  if (/^\d{3,10}$/.test(name)) return name;
  const m = email.match(/^(\d{3,10})@/);
  if (m) return m[1];
  return '';
}

/** Sort by date; keep duplicate same-day / same-amount lines (matches source spreadsheets). */
function sortAndInferTrancheKinds(tranches) {
  if (!Array.isArray(tranches)) return [];
  const filtered = [...tranches].filter((t) => t && Number(t.amount) > 0 && t.startDate);
  const withIdx = filtered.map((t, i) => ({ t, i }));
  withIdx.sort((a, b) => {
    const c = String(a.t.startDate).localeCompare(String(b.t.startDate));
    return c !== 0 ? c : a.i - b.i;
  });
  return withIdx.map(({ t }, i) => ({
    ...t,
    trancheKind: t.trancheKind || (i === 0 ? 'initial' : 'addition'),
  }));
}

/**
 * @param {Array<Record<string, unknown>>} investors
 */
function mergeInvestorsForPreview(investors) {
  const by = new Map();
  const noKey = [];
  let noid = 0;
  for (const inv of investors || []) {
    if (!inv || typeof inv !== 'object') continue;
    const em = typeof inv.email === 'string' ? inv.email.trim().toLowerCase() : '';
    const code = canonicalPartnerCode(inv);
    const name = typeof inv.name === 'string' ? inv.name.trim() : '';
    const identifier = typeof inv.identifier === 'string' ? inv.identifier.trim() : '';
    const key = code ? `p:${code}` : em ? `e:${em}` : name ? `n:${name.slice(0, 120)}` : '';

    const tranches = Array.isArray(inv.capitalTranches) ? [...inv.capitalTranches] : [];
    const displayName = name || identifier || code || '';

    const rowBase = {
      displayName,
      identifier,
      notes: typeof inv.notes === 'string' ? inv.notes : '',
      selected: true,
    };

    if (!key) {
      noKey.push({
        ...rowBase,
        rowKey: `noid-${noid++}`,
        email: em,
        capitalTranches: sortAndInferTrancheKinds(tranches),
      });
      continue;
    }

    if (!by.has(key)) {
      by.set(key, {
        ...rowBase,
        rowKey: key,
        email: em,
        capitalTranches: sortAndInferTrancheKinds(tranches),
      });
    } else {
      const cur = by.get(key);
      cur.capitalTranches = sortAndInferTrancheKinds([...cur.capitalTranches, ...tranches]);
      if (!cur.email && em) cur.email = em;
      if (!cur.displayName && displayName) cur.displayName = displayName;
      if (!cur.identifier && identifier) cur.identifier = identifier;
    }
  }
  return [...by.values(), ...noKey];
}

function sumTranches(tranches) {
  if (!Array.isArray(tranches)) return 0;
  return tranches.reduce((acc, tr) => {
    const n = Number(tr?.amount);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);
}

function roundMoneyToCents(n) {
  return Math.round(n * 100) / 100;
}

export default function FundInvestorsImportModal({
  open,
  onClose,
  lenderUid,
  fundInConfiguration = false,
  existingInvestors,
  t,
  language,
  onImported,
}) {
  const [step, setStep] = useState('pick');
  const [fileLabel, setFileLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [sendInvitation, setSendInvitation] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setStep('pick');
    setFileLabel('');
    setLoading(false);
    setCommitting(false);
    setError('');
    setRows([]);
    setSendInvitation(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const existingEmailSet = useMemo(
    () =>
      new Set(
        (existingInvestors || []).map((x) => String(x.email || '').trim().toLowerCase()).filter(Boolean)
      ),
    [existingInvestors]
  );

  const rowMetas = useMemo(
    () =>
      rows.map((row) => {
        const em = String(row.email || '').trim().toLowerCase();
        const ok = isValidEmail(row.email);
        const hasTranches = Array.isArray(row.capitalTranches) && row.capitalTranches.length > 0;
        const alreadyInApp = ok && existingEmailSet.has(em);
        const canCommit = row.selected && ok && hasTranches && !alreadyInApp;
        return { ok, hasTranches, alreadyInApp, canCommit };
      }),
    [rows, existingEmailSet]
  );

  const hasCommitable = useMemo(() => rowMetas.some((m) => m.canCommit), [rowMetas]);

  const importPreviewTotals = useMemo(() => {
    let all = 0;
    let selected = 0;
    rows.forEach((row) => {
      const s = sumTranches(row.capitalTranches);
      all += s;
      if (row.selected) selected += s;
    });
    return { all: roundMoneyToCents(all), selected: roundMoneyToCents(selected) };
  }, [rows]);

  const pickFile = async (file) => {
    if (!file) return;
    setError('');
    setFileLabel(file.name);
    setLoading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => {
          const res = String(r.result || '');
          const idx = res.indexOf('base64,');
          resolve(idx >= 0 ? res.slice(idx + 7) : res);
        };
        r.onerror = () => reject(new Error('read failed'));
        r.readAsDataURL(file);
      });
      const data = await extractFundInvestorsImport(b64, file.name, language);
      const merged = mergeInvestorsForPreview(data.investors || []);
      if (merged.length === 0) {
        setError(t('fundInvestors.fileImport.noRowsAfterMerge'));
        return;
      }
      setRows(merged);
      setStep('preview');
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (i, patch) => {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };

  const handleCommit = async () => {
    setError('');
    setCommitting(true);
    let created = 0;
    let failed = 0;
    let skipped = 0;
    try {
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        const meta = rowMetas[i];
        if (!row.selected) continue;
        if (!meta.canCommit) {
          if (row.selected && meta.alreadyInApp) skipped += 1;
          else if (row.selected && (!meta.ok || !meta.hasTranches)) failed += 1;
          continue;
        }
        const email = String(row.email || '').trim().toLowerCase();
        const tranches = (row.capitalTranches || [])
          .map((tr) => ({
            amount: Number(tr.amount),
            startDate: String(tr.startDate || '').trim().slice(0, 10),
            note: tr.note != null && String(tr.note).trim() ? String(tr.note).trim().slice(0, 500) : null,
          }))
          .filter(
            (tr) =>
              Number.isFinite(tr.amount) &&
              tr.amount > 0 &&
              tr.startDate &&
              String(tr.startDate).length >= 8
          );
        if (tranches.length === 0) {
          failed += 1;
          continue;
        }
        try {
          await createFundInvestorLegacyImport({
            lenderUid,
            email,
            displayName: row.displayName?.trim() || null,
            tranches,
            sendInvitation: fundInConfiguration ? false : sendInvitation,
          });
          created += 1;
        } catch (e) {
          console.error('Fund investor import row:', e);
          failed += 1;
        }
      }
    } finally {
      setCommitting(false);
    }

    if (typeof onImported === 'function') onImported();

    if (created > 0 && (failed > 0 || skipped > 0)) {
      alert(
        t('fundInvestors.fileImport.partialSummary')
          .replace('{ok}', String(created))
          .replace('{skip}', String(skipped))
          .replace('{bad}', String(failed))
      );
    } else if (created > 0) {
      alert(t('fundInvestors.fileImport.successCount').replace('{n}', String(created)));
    } else if (skipped > 0 && failed === 0) {
      alert(t('fundInvestors.fileImport.onlyDuplicates'));
    } else if (failed > 0) {
      alert(t('fundInvestors.fileImport.nothingCreated'));
    } else {
      alert(t('fundInvestors.fileImport.noSelection'));
    }
    handleClose();
  };

  useRegisterTopEscapeClose(handleClose, open);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{t('fundInvestors.fileImport.title')}</h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-100"
            aria-label={t('close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {step === 'pick' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">{t('fundInvestors.fileImport.hint')}</p>
              <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">{t('fundInvestors.fileImport.expectedTitle')}</p>
                <ul className="list-disc list-outside ms-4 space-y-1.5 text-sm text-gray-700">
                  <li>{t('fundInvestors.fileImport.expectedName')}</li>
                  <li>{t('fundInvestors.fileImport.expectedEmail')}</li>
                  <li>{t('fundInvestors.fileImport.expectedIdentifier')}</li>
                  <li>{t('fundInvestors.fileImport.expectedTranches')}</li>
                  <li>{t('fundInvestors.fileImport.expectedDates')}</li>
                  <li>{t('fundInvestors.fileImport.expectedKind')}</li>
                </ul>
              </div>
              <p className="text-xs text-gray-600">{t('fundInvestors.fileImport.templateRecommended')}</p>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
              <div className="flex flex-wrap gap-2">
                <a
                  href="/templates/fund-investors-import-template.xlsx"
                  download="rentalyzer-fund-investors-import-example.xlsx"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50"
                >
                  <Download className="w-5 h-5" />
                  {t('fundInvestors.fileImport.downloadTemplate')}
                </a>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => inputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border border-indigo-600 text-indigo-700 rounded-lg hover:bg-indigo-50 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  {loading ? t('fundInvestors.fileImport.analyzing') : t('fundInvestors.fileImport.chooseFile')}
                </button>
              </div>
              {fileLabel && <p className="text-xs text-gray-500">{fileLabel}</p>}
            </div>
          )}
          {step === 'preview' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">{t('fundInvestors.fileImport.previewHelp')}</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm justify-end text-gray-800">
                <span>
                  {t('fundInvestors.fileImport.previewTotalAll')}:{' '}
                  <strong className="tabular-nums">{formatMoney(importPreviewTotals.all)}</strong>
                </span>
                <span>
                  {t('fundInvestors.fileImport.previewTotalSelected')}:{' '}
                  <strong className="tabular-nums">{formatMoney(importPreviewTotals.selected)}</strong>
                </span>
              </div>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="p-2 w-10"> </th>
                      <th className="p-2">{t('fundInvestors.fileImport.colDisplayName')}</th>
                      <th className="p-2 min-w-[11rem]">{t('fundInvestors.invite.email')}</th>
                      <th className="p-2">{t('fundInvestors.fileImport.colTotal')}</th>
                      <th className="p-2 min-w-[14rem]">{t('fundInvestors.fileImport.colTranches')}</th>
                      <th className="p-2 w-36">{t('fundInvestors.fileImport.colStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const meta = rowMetas[i];
                      const total = sumTranches(row.capitalTranches);
                      return (
                        <tr key={row.rowKey} className="border-t border-gray-100 align-top">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={row.selected}
                              onChange={(e) => updateRow(i, { selected: e.target.checked })}
                            />
                          </td>
                          <td className="p-2">
                            <input
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              value={row.displayName || ''}
                              onChange={(e) => updateRow(i, { displayName: e.target.value })}
                            />
                          </td>
                          <td className="p-2">
                            <input
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              value={row.email || ''}
                              onChange={(e) => updateRow(i, { email: e.target.value })}
                              placeholder={t('fundInvestors.fileImport.emailPlaceholder')}
                            />
                          </td>
                          <td className="p-2 whitespace-nowrap">{formatMoney(total)}</td>
                          <td className="p-2 text-xs text-gray-700">
                            <ul className="space-y-1">
                              {(row.capitalTranches || []).map((tr, j) => (
                                <li key={j}>
                                  {formatMoney(Number(tr.amount))} — {tr.startDate}{' '}
                                  <span className="text-gray-500">
                                    ({trancheKindLabel(tr.trancheKind, t)})
                                  </span>
                                  {tr.note ? <span className="text-gray-500"> — {tr.note}</span> : null}
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td className="p-2 text-xs">
                            {meta.alreadyInApp && (
                              <span className="text-amber-800">{t('fundInvestors.fileImport.duplicateEmail')}</span>
                            )}
                            {!meta.alreadyInApp && !meta.ok && row.selected && (
                              <span className="text-red-700">{t('fundInvestors.fileImport.badEmail')}</span>
                            )}
                            {!meta.hasTranches && row.selected && (
                              <span className="text-red-700">{t('fundInvestors.fileImport.noTranches')}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <label
                className={`flex items-center gap-2 text-sm ${fundInConfiguration ? 'text-gray-400' : 'text-gray-700'}`}
              >
                <input
                  type="checkbox"
                  checked={sendInvitation}
                  disabled={fundInConfiguration}
                  onChange={(e) => setSendInvitation(e.target.checked)}
                />
                {t('fundInvestors.legacyImport.sendInvitation')}
                {fundInConfiguration && (
                  <span className="text-xs text-gray-500">
                    ({t('fundInvestors.legacyImport.sendInvitationDisabledFundConfig')})
                  </span>
                )}
              </label>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep('pick')}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-800 hover:bg-gray-50"
                >
                  {t('fundInvestors.fileImport.back')}
                </button>
                <button
                  type="button"
                  disabled={committing || !hasCommitable}
                  onClick={handleCommit}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {committing ? t('fundInvestors.invite.sending') : t('fundInvestors.fileImport.commit')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
